import type { Dispatch, SetStateAction } from "react";
import { Button, Card, DatePicker, Input, Select, Switch, Typography } from "@arco-design/web-react";
import { IconInfoCircle } from "@arco-design/web-react/icon";
import { RotateCcw, Search } from "lucide-react";

import { AdvancedFilterToggle } from "../../../../shared/custom-ui";
import type { QueryFormState, SelectOption } from "../types";

const { Text } = Typography;

type MySqlQueryFilterCardProps = {
  formState: QueryFormState;
  isCompact: boolean;
  showAdvancedFilters: boolean;
  errorMessage: string;
  sourceOptions: SelectOption[];
  tagOptions: SelectOption[];
  setFormState: Dispatch<SetStateAction<QueryFormState>>;
  onToggleAdvancedFilters: () => void;
  onReset: () => void;
  onSearch: () => void;
};

/** MySQL 查询列表筛选卡片。 */
export function MySqlQueryFilterCard({
  formState,
  isCompact,
  showAdvancedFilters,
  errorMessage,
  sourceOptions,
  tagOptions,
  setFormState,
  onToggleAdvancedFilters,
  onReset,
  onSearch,
}: MySqlQueryFilterCardProps) {
  return (
    <Card
      className="mysql-query-card"
      bodyStyle={{
        padding: isCompact ? 18 : 24,
      }}
    >
      <div
        data-testid="mysql-query-filter-grid"
        className={isCompact ? "mysql-query-filter-grid is-compact" : "mysql-query-filter-grid"}
      >
        <div className="mysql-query-filter-field">
          <Text className="mysql-query-filter-label">记录名称</Text>
          <Input
            allowClear
            placeholder="请输入记录名称"
            value={formState.recordName}
            onChange={(value) =>
              setFormState((current) => ({ ...current, recordName: String(value || "") }))
            }
          />
        </div>

        <div className="mysql-query-filter-field">
          <Text className="mysql-query-filter-label">数据来源</Text>
          <Select
            className="mysql-query-field-control"
            placeholder="请选择数据来源"
            allowClear
            value={formState.source || undefined}
            options={sourceOptions}
            onChange={(value) =>
              setFormState((current) => ({ ...current, source: typeof value === "string" ? value : "" }))
            }
          />
        </div>

        <div className="mysql-query-filter-field">
          <Text className="mysql-query-filter-label">标签</Text>
          <Select
            className="mysql-query-field-control"
            mode="multiple"
            placeholder="请选择标签"
            allowClear
            value={formState.tags}
            options={tagOptions}
            onChange={(value) =>
              setFormState((current) => ({
                ...current,
                tags: Array.isArray(value) ? value.map((item) => String(item)) : [],
              }))
            }
          />
        </div>
      </div>

      <div
        data-testid="mysql-query-action-bar"
        className={isCompact ? "mysql-query-action-bar is-compact" : "mysql-query-action-bar"}
      >
        <AdvancedFilterToggle expanded={showAdvancedFilters} onToggle={onToggleAdvancedFilters} />

        <div className="mysql-query-action-buttons">
          <Button shape="round" icon={<RotateCcw size={16} strokeWidth={2.2} />} onClick={onReset}>
            重置
          </Button>
          <Button
            type="primary"
            shape="round"
            icon={<Search size={16} strokeWidth={2.2} />}
            onClick={onSearch}
          >
            搜索
          </Button>
        </div>
      </div>

      {showAdvancedFilters ? (
        <div
          data-testid="mysql-query-advanced-panel"
          className={isCompact ? "mysql-query-advanced-grid is-compact" : "mysql-query-advanced-grid"}
        >
          <div className="mysql-query-filter-field">
            <Text className="mysql-query-filter-label">业务日期</Text>
            <DatePicker
              className="mysql-query-field-control"
              placeholder="请选择业务日期"
              prefix={<IconInfoCircle />}
              value={formState.bizDate}
              onChange={(dateString) =>
                setFormState((current) => ({ ...current, bizDate: String(dateString || "") }))
              }
            />
          </div>

          <div className="mysql-query-filter-field">
            <Text className="mysql-query-filter-label">创建日期</Text>
            <DatePicker.RangePicker
              className="mysql-query-field-control"
              placeholder={["开始日期", "结束日期"]}
              prefix={<IconInfoCircle />}
              value={formState.createdRange}
              onChange={(dateStrings) =>
                setFormState((current) => ({
                  ...current,
                  createdRange: dateStrings.filter((item) => item),
                }))
              }
            />
          </div>

          <div className="mysql-query-filter-field">
            <Text className="mysql-query-filter-label">是否启用</Text>
            <div className="mysql-query-enabled-switch-row">
              <Switch
                checked={formState.enabledOnly}
                onChange={(value) =>
                  setFormState((current) => ({
                    ...current,
                    enabledOnly: value,
                  }))
                }
              />
              <Text style={{ color: formState.enabledOnly ? "#165dff" : "#4e5969", fontWeight: 600 }}>
                {formState.enabledOnly ? "是" : "否"}
              </Text>
            </div>
          </div>
        </div>
      ) : null}

      {errorMessage ? (
        <div className="mysql-query-error-message">{errorMessage}</div>
      ) : null}
    </Card>
  );
}
